import Mosaic from 'mosaic-framework';

import '../components/navigation-bar';
import '../components/footer';
import '../components/round-button';
import '../components/code-segment';

import '../styles/docs.less';


export default new Mosaic({
    name: 'lifecycle-page',
    created() {
        window.scrollTo({ top: 0 });

        const menubtn = document.getElementById('menu-button');
        menubtn.style.display = 'block';

        if(menubtn.onclick) return;

        menubtn.onclick = () => {
            const menu = document.getElementById('doc-menu');
            menu.style.transform = 'translateX(0%)';
        }
    },
    view() {
        return html`<div class='doc-page'>
            <navigation-bar></navigation-bar>
            
            <h1 class='section-title' tabindex='0'>🕑 Lifecyle Hooks</h1>
            <p class='section-text' tabindex='0'>
                Mosaic gives you a set of functions that get triggered through the 
                lifecycle of your components. These functions allow you to perform
                actions based around the timing and current state of your component.
                Below we will explore the different types of lifecycle hooks that
                Mosaic uses.
            </p>
            
            <h1 class='section-title' tabindex='0'>created()</h1>
            <p class='section-text' tabindex='0'>
                The first lifcycle hook we will explore is <b>created</b> which gets called
                when a component is injected into the DOM. It will be called after the
                component has had all of its data and attribute setup and after it has been
                repainted for the first time to match the current state. 
            </p>
            <p class='section-text' tabindex='0'>
                An important thing to remember about this function is that it will be called
                every time you insert the component into the DOM. Mosaic uses the Custom
                Elements API and takes advantage of the "connectedCallback" to figure out when
                a component is "connected" to the DOM. This means that the created function
                will be run any time a component is added to the document.
            </p>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;created: function() {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                console.log("Created this component and added it to the page!");
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;},
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                });
            </code-segment>
            <br>

            <h1 class='section-title' tabindex='0'>willUpdate(oldData?: <span style="color:mediumseagreen">Object</span>)</h1>
            <p class='section-text' tabindex='0'>
                Next we will take a look at the <b>willUpdate</b> lifecycle hook, which gets
                called just before a component's data is changed. The function is called with
                an optional parameter representing the old state of the component just before
                it is updated.
            </p>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;willUpdate: function(old) {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                console.log("About to update. Here is the old data: ", old);
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;},
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                });
            </code-segment>
            <br>

            <h1 class='section-title' tabindex='0'>updated()</h1>
            <p class='section-text' tabindex='0'>
                <b>Updated</b> gets called after every single data change. The most important
                thing to remember about this function is to <u><i>never</i></u> update the data
                property inside of this function. This would cause an infinite cycle of "repaints"
                as you update the data, then call the updated function, then update again, and so
                on...
            </p>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;updated: function() {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                console.log("Just finished updating the component!");
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;},
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                });
            </code-segment>
            <br>

            <h1 class='section-title' tabindex='0'>willDestroy</h1>
            <p class='section-text' tabindex='0'>
                When a component is about to be removed from the DOM, it will call its
                <b>willDestroy</b> function. This is useful for removing up event listeners
                and cleaning up other resources before the component leaves the page.
            </p>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;willDestroy: function() {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                console.log("About to destroy this component.");
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                this.removeEventListener('click', this.functionName);
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;},
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                });
            </code-segment>
            <br>

            <h1 class='section-title' tabindex='0'>
                received(attributes: <span style='color:mediumseagreen'>Object</span>)
            </h1>
            <p class='section-text' tabindex='0'>
                <b>Received</b> is an interesting lifecycle function. It gets called
                whenever an attribute is added or modified on a component. When we add
                attributes to a component's custom element tag, it will call the received
                function. It is within that function that we can take certain actions based
                around the included attributes.
            </p>
            <p class='section-text' tabindex='0'>
                Take a look at the following example, where we use an attribute called
                "type" to determine what color to make the button. This can be usedful
                for updating components without having to trigger a repaint, which will
                be explored more in the next section.
            </p>
            <code-segment type='JS'>
                new Mosaic({
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;received: function(attributes) {
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                const { type } = attributes;
                <br><br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                if(type === 'primary') this.style.backgroundColor = 'green';
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                else if(type === 'secondary') this.style.backgroundColor = 'blue';
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                else this.style.backgroundColor = 'gray';
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                },
                <br>
                &nbsp;&nbsp;&nbsp;&nbsp;...
                <br>
                });
            </code-segment>
            <br>

            <br>
            <p class='section-text' tabindex='0' style="text-align: center;">
                Click below to check out the section on observable data and 
                the repaint cycle!
            </p>

            <div style="text-align: center;">
                <round-button onclick='${() => this.router.send('/element')}'>
                    <ion-icon name="arrow-round-back"></ion-icon>
                    Element
                </round-button>
                &nbsp;&nbsp;
                <round-button onclick='${() => this.router.send('/data')}'>
                    Observable Data
                    <ion-icon name="arrow-round-forward"></ion-icon>
                </round-button>
            </div>

            <app-footer></app-footer>
        </div>`
    }
});